import { apiGet, apiPatch, apiPost } from './client.js';
import type { ChecklistItemTemplate } from './checklists.js';

export type DepositMode = 'none' | 'optional' | 'required' | 'rule_based';

export type WaiverCollectionMode = 'customer_display' | 'hand_across' | 'sms_link';

/** Row shape from GET/PATCH `/api/v1/stores/current` (matches API + DB fields used in Settings). */
export type StoreSettings = {
  id: string;
  tenantId?: string;
  name: string;
  slug?: string;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  timezone?: string | null;
  currency?: string | null;
  logoUrl?: string | null;
  storeHours?: unknown;
  taxRate?: string | number | null;
  taxInclusive?: boolean;
  taxMode?: 'parts_only' | 'all' | 'none';
  showPartsOnReceipt?: boolean;
  trackSerialParts?: boolean;
  partSearchMode?: 'all' | 'compatible_only';
  partCompatibilityWarn?: boolean;
  partsReservationMode?: 'disabled' | 'soft' | 'hard' | null;
  partsReservationAutoReleaseDays?: number | null;
  partsReservationShowAvailable?: boolean;
  active?: boolean;
  ticketNumberPrefix?: string | null;
  ticketNumberPadLength?: number | null;
  defaultRepairMinutes?: number | null;
  autoAssignMode?: 'off' | 'creator' | 'fewest_tickets' | 'least_work' | 'round_robin' | null;
  autoAssignClockedInOnly?: boolean | null;
  autoAssignExcludeRoles?: string[] | null;
  preWaiverRequired?: boolean;
  preWaiverSkippableForReturning?: boolean;
  postWaiverRequired?: boolean;
  waiverCollectionMode?: string | null;
  intakeRequireImei?: boolean;
  intakeRequireDevicePhoto?: boolean;
  defaultWarrantyDays?: number | null;
  defaultWarrantyType?: 'days' | 'lifetime' | 'none' | null;
  depositMode?: string | null;
  depositRequiredAmount?: string | number | null;
  depositRequiredPct?: string | number | null;
  depositRulePartsOrder?: boolean;
  depositRuleMinAmount?: string | number | null;
  depositRefundable?: boolean;
  overdueAlertHours?: number | null;
  lowStockNotifyEmail?: string | null;
  showEstimatedProfitRoles?: string[] | null;
  telnyxPhoneNumber?: string | null;
  smsOnReady?: boolean;
  smsOnWaitingParts?: boolean;
  smsOnCancelled?: boolean;
  smsOnCheckout?: boolean;
  smsOnTicketCreated?: boolean;
  smsTemplateReady?: string | null;
  smsTemplateWaitingParts?: string | null;
  smsTemplateCancelled?: string | null;
  smsTemplateCheckout?: string | null;
  smsTemplateTicketCreated?: string | null;
  emailOnClosed?: boolean;
  emailOnCreate?: boolean;
  emailOnReady?: boolean;
  emailFromName?: string | null;
  displayRequiredFields?: Record<string, boolean> | null;
  displayIdleMessage?: string | null;
  displayShowTaxLine?: boolean;
  displayShowDevicePin?: boolean;
  displayShowDeviceCondition?: boolean;
  openingChecklistEnabled?: boolean;
  closingChecklistEnabled?: boolean;
  commsDryRun?: boolean;

  blockyypTerminalName?: string | null;
  blockchypConfig?: {
    tipEnabled?: boolean;
    signatureRequired?: boolean;
  } | null;

  cashDiscountEnabled?: boolean;
  cashDiscountRate?: string | number | null;
  cashDiscountMode?: 'card_surcharge' | 'cash_discount' | string | null;
  cashDiscountLabel?: string | null;
  cashDiscountShowOnReceipt?: boolean;
  terminalAppliesSurcharge?: boolean;
  tipsEnabled?: boolean;
  tipsOnCard?: boolean;
  tipsOnCash?: boolean;
  tipsRequirePin?: boolean;
  tipAttribution?: 'checkout_staff' | 'assigned_tech' | 'split' | string | null;
  tipSplitConfig?: { checkout?: number; tech?: number } | null;

  cashDenominations?: {
    bills?: Array<{ key: string; valueCents: number; label: string; enabled: boolean }>;
    coins?: Array<{ key: string; valueCents: number; label: string; enabled: boolean }>;
    rollValues?: Record<string, number>;
  } | null;
  trackCoinRolls?: boolean;
  cashVarianceTolerance?: string | number | null;
  cashVarianceRequiresOverride?: boolean;
  changeAlertsEnabled?: boolean;
  changeMinimums?: Record<string, number> | null;
  largeBillThreshold?: string | number | null;
  dropThreshold?: string | number | null;
  changeAlertsSmsOnCritical?: boolean;
  changeAlertDedupeHours?: number | null;
  dropAlertMode?: 'never' | 'on_close' | 'realtime' | null;

  printnodeLabelPrinterId?: number | null;
  printnodeReceiptPrinterId?: number | null;
  labelConfig?: {
    showBarcode?: boolean;
    showTech?: boolean;
    showPromisedDate?: boolean;
    showPhone?: boolean;
    copies?: number;
    labelSizePreset?: string;
    labelWidthIn?: number;
    labelHeightIn?: number;
  } | null;
  inventoryLabelConfig?: Record<string, unknown> | null;
  autoLabelOnReceive?: boolean;
  queueEnabled?: boolean;
  queueDisplayNameMode?:
    | 'first_name'
    | 'first_last_init'
    | 'initials'
    | 'number'
    | 'full_name'
    | null;
  queueDisplayShowDevice?: boolean;
  queueDisplayShowReason?: boolean;
  queueAutoAbandonMinutes?: number | null;
  queueDefaultServiceMinutes?: number | null;
  queueAudioAlert?: boolean;
  receiptConfig?: {
    showLogo?: boolean;
    showPortalLink?: boolean;
    thankYouMessage?: string;
    copies?: number;
  } | null;
  posCategoriesEnabled?: string[] | null;
  posShowOutOfStock?: boolean;
  posRequireCustomer?: boolean;
  posDefaultCategory?: string | null;
  posPinnedItems?: string[] | null;
  portalDefaultCanCreateTickets?: boolean;
  estimateDefaultValidHours?: number | null;
  estimateRequireSignature?: boolean;
  estimateAutoSendVia?: string | null;

  // Goals (Sprint 6)
  dailyRevenueGoal?: string | number | null;
  weeklyRevenueGoal?: string | number | null;
  monthlyRevenueGoal?: string | number | null;
  avgLaborRateGoal?: string | number | null;

  // Security / PIN requirements (Sprint 6)
  pinRequiredFor?: string[];
  requirePinForCash?: boolean;
  requirePinForRefunds?: boolean;
  requirePinForVoids?: boolean;
  requirePinForDiscounts?: boolean;

  // Referral program (Sprint 6) — matches stores.referral_* columns / patchStoreSchema
  referralEnabled?: boolean;
  referralReferrerPoints?: number | null;
  referralReferredPoints?: number | null;
  referralReferrerRewardType?: 'points' | 'store_credit' | null;
  referralReferredRewardType?: 'points' | 'store_credit' | null;
  referralReferrerCredit?: string | number | null;
  referralReferredCredit?: string | number | null;
  referralTrigger?: 'first_visit' | 'first_purchase' | 'manual' | null;
  referralNotifyReferrer?: boolean;

  // Sprint 7 — checklists & care guides
  openingChecklistItems?: ChecklistItemTemplate[] | null;
  closingChecklistItems?: ChecklistItemTemplate[] | null;
  sendCareGuideOnCheckout?: boolean;
  careGuideSendVia?: 'email' | 'sms' | 'both' | null;

  // Sprint 8 — feature flags
  featureFlags?: Record<string, unknown> | null;
  approvalSettings?: Record<string, unknown> | null;

  // Sprint 9 — M360
  m360AutoRetrieveOnImei?: boolean;
  m360ShowInPortal?: boolean;

  // Customer status labels
  vipLabel?: string | null;
  flaggedLabel?: string | null;
};

export type PosCategoryTab = 'all' | 'repairs' | 'accessories' | 'unlocking' | 'trade_in' | 'misc';

export const DEFAULT_POS_CATEGORIES: PosCategoryTab[] = [
  'repairs',
  'accessories',
  'unlocking',
  'trade_in',
  'misc',
];

export type StoreSettingsUpdate = Partial<{
  name: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  timezone: string;
  currency: string;
  logoUrl: string | null;
  storeHours: unknown;
  taxRate: number;
  taxInclusive: boolean;
  taxMode: 'parts_only' | 'all' | 'none';
  showPartsOnReceipt: boolean;
  trackSerialParts: boolean;
  partSearchMode: 'all' | 'compatible_only';
  partCompatibilityWarn: boolean;
  partsReservationMode: 'disabled' | 'soft' | 'hard';
  partsReservationAutoReleaseDays: number;
  partsReservationShowAvailable: boolean;
  ticketNumberPrefix: string;
  ticketNumberPadLength: number;
  defaultRepairMinutes: number;
  autoAssignMode?: 'off' | 'creator' | 'fewest_tickets' | 'least_work' | 'round_robin';
  autoAssignClockedInOnly?: boolean;
  autoAssignExcludeRoles?: string[];
  preWaiverRequired: boolean;
  preWaiverSkippableForReturning: boolean;
  postWaiverRequired: boolean;
  waiverCollectionMode: WaiverCollectionMode;
  intakeRequireImei: boolean;
  intakeRequireDevicePhoto: boolean;
  defaultWarrantyDays: number;
  defaultWarrantyType: 'days' | 'lifetime' | 'none';
  depositMode: DepositMode;
  depositRequiredAmount: number;
  depositRequiredPct: number;
  depositRulePartsOrder: boolean;
  depositRuleMinAmount: number;
  depositRefundable: boolean;
  overdueAlertHours: number;
  lowStockNotifyEmail: string | null;
  showEstimatedProfitRoles: string[];
  telnyxPhoneNumber: string | null;
  smsOnReady: boolean;
  smsOnWaitingParts: boolean;
  smsOnCancelled: boolean;
  smsOnCheckout: boolean;
  smsOnTicketCreated: boolean;
  smsTemplateReady: string;
  smsTemplateWaitingParts: string;
  smsTemplateCancelled: string;
  smsTemplateCheckout: string;
  smsTemplateTicketCreated: string;
  emailOnClosed: boolean;
  emailOnCreate: boolean;
  emailOnReady: boolean;
  emailFromName: string | null;
  displayRequiredFields: {
    phone: boolean;
    lastName: boolean;
    email: boolean;
    address: boolean;
    cityStateZip: boolean;
  };
  displayIdleMessage: string | null;
  displayShowTaxLine?: boolean;
  displayShowDevicePin?: boolean;
  displayShowDeviceCondition?: boolean;
  openingChecklistEnabled?: boolean;
  closingChecklistEnabled?: boolean;

  blockyypTerminalName?: string | null;
  blockchypConfig?: {
    tipEnabled?: boolean;
    signatureRequired?: boolean;
  };

  cashDiscountEnabled?: boolean;
  cashDiscountRate?: number | null;
  cashDiscountMode?: 'card_surcharge' | 'cash_discount';
  cashDiscountLabel?: string | null;
  cashDiscountShowOnReceipt?: boolean;
  terminalAppliesSurcharge?: boolean;
  tipsEnabled?: boolean;
  tipsOnCard?: boolean;
  tipsOnCash?: boolean;
  tipsRequirePin?: boolean;
  tipAttribution?: 'checkout_staff' | 'assigned_tech' | 'split';
  tipSplitConfig?: { checkout?: number; tech?: number };
  cashDenominations?: {
    bills?: Array<{ key: string; valueCents: number; label: string; enabled: boolean }>;
    coins?: Array<{ key: string; valueCents: number; label: string; enabled: boolean }>;
    rollValues?: Record<string, number>;
  } | null;
  trackCoinRolls?: boolean;
  cashVarianceTolerance?: number;
  cashVarianceRequiresOverride?: boolean;
  changeAlertsEnabled?: boolean;
  changeMinimums?: Record<string, number>;
  largeBillThreshold?: number;
  dropThreshold?: number;
  changeAlertsSmsOnCritical?: boolean;
  changeAlertDedupeHours?: number;
  dropAlertMode?: 'never' | 'on_close' | 'realtime';

  printnodeLabelPrinterId: number | null;
  printnodeReceiptPrinterId: number | null;
  labelConfig: {
    showBarcode?: boolean;
    showTech?: boolean;
    showPromisedDate?: boolean;
    showPhone?: boolean;
    copies?: number;
    labelSizePreset?: string;
    labelWidthIn?: number;
    labelHeightIn?: number;
  };
  inventoryLabelConfig?: Record<string, unknown>;
  autoLabelOnReceive?: boolean;
  queueEnabled?: boolean;
  queueDisplayNameMode?:
    | 'first_name'
    | 'first_last_init'
    | 'initials'
    | 'number'
    | 'full_name';
  queueDisplayShowDevice?: boolean;
  queueDisplayShowReason?: boolean;
  queueAutoAbandonMinutes?: number;
  queueDefaultServiceMinutes?: number;
  queueAudioAlert?: boolean;
  receiptConfig: {
    showLogo?: boolean;
    showPortalLink?: boolean;
    thankYouMessage?: string;
    copies?: number;
  };
  posCategoriesEnabled?: PosCategoryTab[];
  posShowOutOfStock?: boolean;
  posRequireCustomer?: boolean;
  posDefaultCategory?: PosCategoryTab | 'all';
  posPinnedItems?: string[];
  portalDefaultCanCreateTickets?: boolean;
  estimateDefaultValidHours?: number;
  estimateRequireSignature?: boolean;
  estimateAutoSendVia?: 'email' | 'sms' | 'both' | 'none';

  // Goals (Sprint 6)
  dailyRevenueGoal: number | null;
  weeklyRevenueGoal: number | null;
  monthlyRevenueGoal: number | null;
  avgLaborRateGoal: number | null;

  // Security / PIN requirements (Sprint 6)
  pinRequiredFor: string[];
  requirePinForCash: boolean;
  requirePinForRefunds: boolean;
  requirePinForVoids: boolean;
  requirePinForDiscounts: boolean;

  // Referral program (Sprint 6)
  referralEnabled: boolean;
  referralReferrerPoints: number;
  referralReferredPoints: number;
  referralReferrerRewardType: 'points' | 'store_credit';
  referralReferredRewardType: 'points' | 'store_credit';
  referralReferrerCredit: number;
  referralReferredCredit: number;
  referralTrigger: 'first_visit' | 'first_purchase' | 'manual';
  referralNotifyReferrer: boolean;

  // Sprint 7 — care guides
  sendCareGuideOnCheckout: boolean;
  careGuideSendVia: 'email' | 'sms' | 'both';

  // Sprint 8 — feature flags
  featureFlags: Record<string, unknown>;
  approvalSettings?: Record<string, unknown>;

  // Sprint 9 — M360
  m360AutoRetrieveOnImei: boolean;
  m360ShowInPortal: boolean;

  vipLabel: string;
  flaggedLabel: string;
}>;

export interface StoreRow {
  id: string;
  name: string;
  tenantId: string;
  active: boolean;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  timezone?: string | null;
  taxRate?: string | number | null;
  ticketNumberPrefix?: string | null;
}

export function createStore(body: {
  name: string;
  phone: string;
  email?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  timezone?: string;
  ticketNumberPrefix?: string;
  taxRate?: number;
}) {
  return apiPost<StoreRow, typeof body>('/api/v1/stores', body).then((r) => r.data);
}

export function updateStoreById(
  storeId: string,
  body: Partial<{
    name: string;
    phone: string;
    email: string | null;
    address: string;
    city: string;
    state: string;
    zip: string;
    timezone: string;
    ticketNumberPrefix: string;
    taxRate: number;
    active: boolean;
  }>,
) {
  return apiPatch<StoreRow, typeof body>(`/api/v1/stores/${storeId}`, body).then((r) => r.data);
}

export function getCurrentStore() {
  return apiGet<StoreSettings>('/api/v1/stores/current').then((r) => r.data);
}

export function listStores() {
  return apiGet<StoreRow[]>('/api/v1/stores').then((r) => r.data);
}

export function updateCurrentStore(body: StoreSettingsUpdate) {
  return apiPatch<StoreSettings, StoreSettingsUpdate>('/api/v1/stores/current', body).then((r) => r.data);
}

export function updateChecklistItems(body: {
  type: 'open' | 'close';
  items: ChecklistItemTemplate[];
}) {
  return apiPatch<StoreSettings, typeof body>('/api/v1/stores/current/checklist-items', body).then(
    (r) => r.data,
  );
}

export function sendTestSms(body: { phone: string }) {
  return apiPost<{ success: boolean; sent: boolean; to: string; messageId: string | null }>(
    '/api/v1/stores/test-sms',
    body,
  ).then((r) => r.data);
}

export function sendTestEmail(body: { email: string }) {
  return apiPost<{ success: boolean; messageId: string | null; to: string; from: string }>(
    '/api/v1/stores/test-email',
    body,
  ).then((r) => r.data);
}

export type NotificationTestTemplateKey =
  | 'sms_ticket_created'
  | 'sms_ready'
  | 'sms_waiting_parts'
  | 'sms_cancelled'
  | 'sms_checkout'
  | 'email_create'
  | 'email_ready'
  | 'email_closed';

export function sendNotificationTestToMe(body: {
  templateKey: NotificationTestTemplateKey;
  ticketId?: string | null;
  templateOverride?: string | null;
}) {
  return apiPost<{
    channel: 'sms' | 'email';
    to: string;
    bodyPreview: string;
    ticketId: string;
    ticketNumber: string;
    dryRun: boolean;
    status: string;
  }>('/api/v1/stores/current/notifications/test', body).then((r) => r.data);
}
